import {
  Edit2,
  Building2,
  Database,
  FileText,
  Layers,
  Trash2,
  Check,
  ChevronDown,
  Video,
  Music,
} from "lucide-react";
import { SearchBar, StatusBadge, EmptyState } from "../../index.jsx";
import { filterWorkspaces } from "../../utils/filter.js";
import ConfirmDeleteModal from "./ConfirmDeleteModal.jsx";
import { workspaceService } from "../../../../services/index.js";
import { useState, useEffect, useRef } from "react";
import Pagination from "../../../common/Pagination.jsx";

const appendWorkspaceDebugLog = (event, payload) => {
  try {
    const key = "workspace_debug_log";
    const entry = { ts: new Date().toISOString(), event, payload };
    const prev = JSON.parse(localStorage.getItem(key) || "[]");
    const next = [...prev, entry].slice(-100);
    localStorage.setItem(key, JSON.stringify(next));
    console.log(`[workspace-debug] ${event}`, entry);
  } catch (error) {
    console.error("workspace debug log failed:", error);
  }
};

export default function WorkspacesTable({
  workspaces,
  searchQuery,
  setSearchQuery,
  setIsWorkspaceOnboarding,
  setSelectedWorkspaceDetails,
  fetchWorkWorkspaces,
}) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [workspace, setWorkspace] = useState(null);
  const [sortOption, setSortOption] = useState("latest");
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const sortDropdownRef = useRef(null);

  // ✅ Toast state
  const [toast, setToast] = useState({ visible: false, message: "", type: "success" });

  const showToast = (message, type = "success") => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast({ visible: false, message: "", type: "success" }), 1500);
  };

  const filtered = filterWorkspaces(workspaces, searchQuery);

  const sortedData = [...filtered].sort((a, b) => {
    if (sortOption === "name") {
      return (a.useCaseName || "").localeCompare(b.useCaseName || "");
    }
    const dateA = new Date(a.updatedDate || a.createdDate || a.modifiedDate || 0).getTime();
    const dateB = new Date(b.updatedDate || b.createdDate || b.modifiedDate || 0).getTime();
    if (dateA === dateB) return (b.useCaseId || 0) - (a.useCaseId || 0);
    return dateB - dateA;
  });

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, itemsPerPage, sortOption]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setIsSortOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const sortOptions = [
    { value: "latest", label: "Latest" },
    { value: "name", label: "Name (A-Z)" },
  ];

  const activeSortLabel =
    sortOptions.find((option) => option.value === sortOption)?.label || "Latest";

  const handleSelect = async (id, for_delete = false, for_edit = false, workspace_detail) => {
    setWorkspace(workspace_detail);
    if (for_delete) {
      setShowDeleteModal(true);
      return;
    }
    if (for_edit) {
      try {
        const workSpaceResponse = await workspaceService.getById(id);
        appendWorkspaceDebugLog("getById_response", {
          id,
          workspace_detail: workspace_detail || null,
          response: workSpaceResponse || null,
        });
        if (workSpaceResponse?.data) {
          setIsWorkspaceOnboarding(true);
          setSelectedWorkspaceDetails(workSpaceResponse.data);
          return;
        }
      } catch (error) {
        appendWorkspaceDebugLog("getById_error", {
          id,
          message: error?.message || "unknown error",
          error,
        });
        console.error("Get workspace by ID error:", error);
      }
      setIsWorkspaceOnboarding(true);
      setSelectedWorkspaceDetails(workspace_detail);
      return;
    }
    setSelectedWorkspaceDetails(workspace_detail);
  };

  const handleDeleteConfirm = async () => {
    try {
      await workspaceService.delete({
        useCaseId: workspace.useCaseId,
        deletedBy: localStorage.getItem("userId"),
      });
      setShowDeleteModal(false);
      fetchWorkWorkspaces();
      showToast("Workspace deleted successfully", "success"); // ✅ delete success
    } catch (error) {
      console.error("Error deleting workspace:", error);
      setShowDeleteModal(false);
      showToast("Failed to delete workspace. Please try again.", "error"); // ✅ delete error
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-[2rem] shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden flex flex-col h-auto max-h-[calc(100vh-140px)] animate-in fade-in slide-in-from-bottom-4 duration-500">

      {/* Header */}
      <div className="p-5 border-b border-slate-100 dark:border-slate-700 flex flex-col lg:flex-row lg:items-center justify-between gap-4 shrink-0">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-white ml-2">
          Workspace Directory
        </h3>
        <div className="flex flex-wrap items-center justify-start lg:justify-end gap-3 w-full lg:w-auto">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
          />
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search workspaces or companies…"
            inputClassName="sm:w-64"
          />
          <div className="relative" ref={sortDropdownRef}>
            <button
              type="button"
              onClick={() => setIsSortOpen((prev) => !prev)}
              className="h-10 min-w-[6.5rem] pl-3 pr-7 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full text-sm font-medium text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600 transition-colors flex items-center justify-start"
            >
              {activeSortLabel}
              <ChevronDown
                size={16}
                className={`absolute right-2.5 text-slate-600 dark:text-slate-300 transition-transform ${isSortOpen ? "rotate-180" : ""}`}
              />
            </button>

            {isSortOpen && (
              <div className="absolute z-20 mt-2 min-w-[6.5rem] overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setSortOption(option.value);
                      setIsSortOpen(false);
                    }}
                    className={`w-full px-3.5 py-2.5 text-left text-sm transition-colors ${
                      sortOption === option.value
                        ? "bg-[#1d57a5] text-white"
                        : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="w-full flex-1 overflow-auto hide-scrollbar">
        <table className="w-full text-left border-collapse min-w-[800px]">
          <thead>
            <tr className="bg-slate-50/50 dark:bg-slate-700/50 border-b border-slate-100 dark:border-slate-700 text-slate-500 dark:text-slate-400 text-sm">
              <th className="px-6 py-5 font-semibold whitespace-nowrap">Workspace Name</th>
              <th className="px-6 py-5 font-semibold whitespace-nowrap">Company Assigned</th>
              <th className="px-6 py-5 font-semibold whitespace-nowrap">Data Sources Configured</th>
              <th className="px-6 py-5 font-semibold whitespace-nowrap">Status</th>
              <th className="px-6 py-5 font-semibold text-right whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {paginatedData.length > 0 ? (
              paginatedData.map((w) => (
                <tr
                  key={w.useCaseId}
                  className="hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition-colors group cursor-pointer"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#36a852]/10 text-[#36a852] dark:bg-[#36a852]/20 flex items-center justify-center font-bold text-sm">
                        <Layers size={18} />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-800 dark:text-slate-200 whitespace-nowrap group-hover:text-[#1d57a5] dark:group-hover:text-[#4a8ce3] transition-colors">
                          {w.useCaseName}
                        </div>
                        <div className="text-slate-500 dark:text-slate-400 text-xs mt-0.5 whitespace-nowrap max-w-[200px] truncate">
                          {w.desc || ""}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Building2 size={14} className="text-slate-400" />
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        {w.companyName}
                      </span>
                    </div>
                  </td>
                   <td className="px-6 py-4">
  <div className="flex items-center gap-3 flex-wrap">
    <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded border border-slate-200 dark:border-slate-700">
      <Database size={12} className="text-amber-500" /> {w.sqlConfigured} Tables
    </span>
    <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded border border-slate-200 dark:border-slate-700">
      <FileText size={12} className="text-[#36a852]" /> {w.fileConfigured} Files
    </span>
    <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded border border-slate-200 dark:border-slate-700">
      <Video size={12} className="text-purple-500" /> {w.mediaConfigured || 0} Media
    </span>
  </div>
</td>
                  <td className="px-6 py-4">
                    <StatusBadge status={w.useCaseStatus} />
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end">
                      <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-1 shadow-sm">
                        <button
                          className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                          title="Edit"
                          onClick={() => handleSelect(w.useCaseId, false, true, w)}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg transition-colors"
                          title="Delete"
                          onClick={() => handleSelect(w.useCaseId, true, false, w)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5}>
                  <EmptyState query={searchQuery} label="workspaces" />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Delete Modal */}
      {workspace && (
        <ConfirmDeleteModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
          useCaseName={`${workspace.useCaseName}`}
        />
      )}

      {/* ✅ Toast — success green / error red */}
      {toast.visible && (
        <div className="fixed top-6 right-6 z-[9999] bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300">
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
              toast.type === "success" ? "bg-[#36a852]" : "bg-red-500"
            }`}
          >
            {toast.type === "success" ? (
              <Check size={14} strokeWidth={3} />
            ) : (
              <span style={{ fontSize: "14px", fontWeight: "bold", lineHeight: 1 }}>✕</span>
            )}
          </div>
          <span className="text-sm font-semibold">{toast.message}</span>
        </div>
      )}
    </div>
  );
}
